import manifest from './clinical-slate-manifest.json';
import { isClinicalSlateSkin, SKIN_ATTR, SKIN_CLINICAL_SLATE } from './skin-flag';

/*
 * QLearn theme token value-sets — DUAL value-set (QLN Clinical Slate tranche T1,
 * web token export, DARK). Contract EXT-15C1 / signer's ruling C2.
 *
 *   DEFAULT scope (skin flag UNSET)  → `shipped` below: the CURRENT shipped values,
 *     BYTE-IDENTICAL to the pre-PR themes.js. Rendered output is unchanged in both
 *     themes, every surface (invariant I1 DARK). Do NOT edit a value in `shipped`.
 *   SKIN scope   (skin flag SET)     → `skin` below: the Clinical Slate PICKED
 *     value-set, built from the C1 manifest (clinical-slate-manifest.json), which
 *     is byte-copied from the pinned design spec (I2 SPEC IS SOURCE). Reachable
 *     ONLY behind the opt-in flag (src/constants/skin-flag.js); never customer-
 *     visible this tranche.
 *
 * Consumers read tokens through CSS custom properties (`var(--<key>)`); the theme
 * object's keys are applied as `--<key>` on document.body by the theme switcher
 * (exam-header.jsx / menu.jsx). Selecting the value-set happens HERE, once, via the
 * flag — so every consumer indirection is scope-correct with no consumer change.
 *
 * The `themes.light` / `themes.dark` getters read the flag at ACCESS time, so a
 * staging probe or the parity harness can flip the root attribute and re-apply the
 * theme without a rebuild. With the flag unset (the only customer-reachable state)
 * they return the shipped value-set unconditionally.
 */

// ── DEFAULT scope — pre-PR shipped values, byte-identical. ────────────────────
const shipped = {
    light: {
        'background-color': '#fff',
        'text-color': '#000',
        'label-color': '#272727',
        'border-color': '#c4c4c4',
        'background-image': 'linear-gradient(#fcfcfc 30%, #d7dced 100%)',
        'action-icon': '#0000008a',
        'link': '#2851bf',
        // Review-queue grounding-pointer cards sit on this panel and render normal
        // text (citation, source kind, snippet, the empty-reference note) plus a link,
        // ALL at --text-color AND AT FULL OPACITY: #000 on #e0e0e0 = 15.91:1, clears
        // 4.5:1 AA. Opacity is called out because it composites over the panel and
        // silently lands the element somewhere other than the value named here.
        'list-bkg': '#e0e0e0',
        'active-row': '#e4f3ff',
        'active-green': '#2ea86c',
        // ABSITE diagnostic report accents. Light keeps the original design hues on
        // the #e0e0e0 (--list-bkg) panels: teal score ~4.1:1 (46px large text, ≥3:1)
        // and amber panel icons ~3.8:1 (UI graphic, ≥3:1). The dark values are
        // lightened tints clearing ≥3:1 on the dark #6d6d6d panel, where the original
        // hues sat at ~1.05:1 — effectively invisible. The eyebrow sits on the page
        // gradient (not a panel) so it keeps --diag-teal (clears 4.5:1 there). The
        // on-panel normal-text accents (plan-day, weak-area %, save/program-code)
        // route to --text-color: no perceptible hue clears 4.5:1 on mid-grey #6d6d6d.
        'diag-teal': '#0f766e',
        'diag-amber': '#b45309',
        // Secondary/hint text. #989898 was only ~2.9:1 on white (fails WCAG AA);
        // #757575 (MUI's textSecondary, ~4.5:1) keeps the de-emphasized grey look
        // while meeting AA. Consumers: exam-prefs & share-earn modals, subscriptions
        // cancel/trial hints — all secondary text on light surfaces.
        'sub-text': '#757575',
        'blue-border': '#0303c0',
        'table-header': '#eaeaea',
        'icon-opacity': 0.2,
        'bkg-text-color': '#fff',
        'correct-ans-color': '#EFFFE8',
        'wrong-ans-color': '#FFE6E6',
        // Form error box (e.g. exam-prefs mandatory warning). Light keeps the
        // existing pink; dark uses a muted dark-red so it isn't a jarring bright
        // box on the dark surface.
        'error-bg': '#ffebee',
        'error-border': '#ffcdd2',
        // Error text. MUI's default error red (#f44336) is only ~3.2:1 on the
        // pink box (fails WCAG AA). #c62828 (MUI red 800) is ~4.9:1 on #ffebee
        // and ~5.4:1 on white; the dark value is a light coral clearing AA on
        // the dark surfaces (~6.6:1).
        'error-text': '#c62828',
        'list-item-sel-color': '#3f51b5',
        'exp-bkg-color': '#fff5ca',
        // Correctness-icon accents (exam-question-container .correct-icon /
        // .incorrect-icon, applied globally). Light uses saturated hues that
        // clear >=3:1 on white AND on the pale correct/wrong answer bands
        // (#1a7f37 ~4.9:1 on #EFFFE8, ~5.1:1 on white; #c62828 ~4.7:1 on
        // #FFE6E6, ~5.6:1 on white). Replaces the raw `green`/`red` keywords
        // (pure green was ~3.0:1 on the dark surface and not theme-aware).
        'correct-icon-color': '#1a7f37',
        'incorrect-icon-color': '#c62828',
        // Power-Search upsell container (create-quiz power-search-panel). Light
        // keeps the original blue-tint literals; the dark variant is a lighter
        // translucent blue so the monetization container stays visible on the
        // dark surface instead of dissolving.
        'power-upsell-bg': 'rgba(30,79,214,0.06)',
        'power-upsell-border': 'rgba(30,79,214,0.2)',
        // ReviewQueue rail status chips. Light keeps the original pastel bg/text
        // pairs (all >=4.5:1); the dark variants are deep-tinted surfaces with
        // light text (all >=8.5:1) so the chips read on-system on the dark rail
        // instead of bright pastel islands.
        'rq-status-published-bg': '#e3f4e6',
        'rq-status-published-text': '#1d6b2c',
        'rq-status-approved-bg': '#e8f0fe',
        'rq-status-approved-text': '#1a4fa3',
        'rq-status-rejected-bg': '#fdeaea',
        'rq-status-rejected-text': '#9c2b2b',
        'rq-status-retracted-bg': '#fff3df',
        'rq-status-retracted-text': '#92600e'
    },
    dark: {
        'background-color': '#262626',
        'text-color': '#fff',
        'label-color': '#fff',
        'border-color': '#e6e6e6',
        'background-image': 'linear-gradient(#313030, #0c0c0c 100%)',
        'action-icon': '#c7c7c78a',
        'link': '#fff',
        // Grounding-pointer cards (see the light-theme note) render their normal text
        // and link at --text-color, full opacity: #fff on #6d6d6d = 5.17:1, clears 4.5:1
        // AA. At opacity .75 the same token composites to 3.74:1 and FAILS. They do
        // NOT use --sub-text (3.99:1 here) or --list-item-sel-color (1.63:1 -- the
        // accent case this file's diagnostic note already warns about).
        'list-bkg': '#6d6d6d',
        'active-row': '#5b95c3',
        'active-green': '#1b6b00',
        'diag-teal': '#5eead4',
        'diag-amber': '#fcd34d',
        'sub-text': '#e2e2e2',
        'blue-border': '#e6e6e6',
        'table-header': '#aeaeae',
        'icon-opacity': 0.1,
        // Answer-highlight bands behind the review drawer's "Detailed Guidance"
        // header + tabs (exam-review-drawer .correct-ans / .wrong-ans). The old
        // pale tints (#d8e7d1/#d8baba) carried forced-white drawer text at
        // ~1.3:1 / ~1.8:1 -- near-unreadable (render-confirmed 2026-07-10, C4).
        // These are now true dark, desaturated green/red surfaces: white text
        // clears ~12:1 / ~13.5:1 while the hue still signals correct/wrong.
        // Light values are unchanged (black text on the pale bands).
        'correct-ans-color': '#1f3d29',
        'wrong-ans-color': '#4a2323',
        // Correctness-icon accents on the dark surfaces/bands (>=3:1 graphic
        // threshold): #58d68d ~6.5:1 on the dark correct band, #ff6b6b ~4.9:1
        // on the dark wrong band.
        'correct-icon-color': '#58d68d',
        'incorrect-icon-color': '#ff6b6b',
        'power-upsell-bg': 'rgba(120,160,255,0.10)',
        'power-upsell-border': 'rgba(120,160,255,0.35)',
        'rq-status-published-bg': '#12351c',
        'rq-status-published-text': '#a7f0ba',
        'rq-status-approved-bg': '#14294d',
        'rq-status-approved-text': '#a9c9ff',
        'rq-status-rejected-bg': '#3f1a1a',
        'rq-status-rejected-text': '#ffb3b3',
        'rq-status-retracted-bg': '#3a2c12',
        'rq-status-retracted-text': '#ffd79a',
        'error-bg': '#4a2222',
        'error-border': '#6e3535',
        'error-text': '#ff8a80',
        'list-item-sel-color': '#7a8ae7',
        // Key-points ("brief explanation") box behind the review drawer AND the
        // ReviewQueue key-points box (review-queue .rq-qdetail-keypoints). The
        // old #5b005b was an off-system purple; now a muted dark amber-olive
        // (white text ~12.5:1) echoing the light #fff5ca highlight semantic
        // while staying on the dark palette.
        'exp-bkg-color': '#3a3320'
    }
};

// ── SKIN scope — Clinical Slate PICKED values, built from the C1 manifest. ────
// Single source of truth: clinical-slate-manifest.json (byte-copied from the
// pinned spec). CHECK1 (scripts/parity/check-clinical-slate.mjs) asserts this
// build equals the manifest and the manifest equals the pinned spec.
function buildSkinScope() {
    const light = {};
    const dark = {};
    for (const r of manifest.colorRoles) {
        light[r.webKey] = r.light;
        dark[r.webKey] = r.dark;
    }
    // Legacy key with no Clinical Slate role — carried unchanged so it still
    // resolves in the skin scope (C3: every pre-PR key keeps resolving).
    light['icon-opacity'] = shipped.light['icon-opacity'];
    dark['icon-opacity'] = shipped.dark['icon-opacity'];
    // Gradient DROP alias (C3): background-image => `none` in the SKIN scope only;
    // the default scope keeps the shipped gradient. No other gradient alias exports.
    for (const a of manifest.aliases) {
        if (a.webKey === 'background-image') {
            light[a.webKey] = a.skinValue;
            dark[a.webKey] = a.skinValue;
        }
    }
    return { light, dark };
}

const skin = buildSkinScope();

// Public API — unchanged shape (`themes.light` / `themes.dark`). The getter picks
// the value-set from the flag at access time; unset (default) always → shipped.
const themes = {
    get light() { return isClinicalSlateSkin() ? skin.light : shipped.light; },
    get dark() { return isClinicalSlateSkin() ? skin.dark : shipped.dark; }
};

// Union of every key either value-set can define. applyThemeVars uses it to REMOVE
// keys absent from the newly-applied scope, so a runtime SKIN→shipped reapply is
// reversible in-document (T3a leg-2 cure B5). Without this, skin-only keys (accent/
// success/border-input/focus-ring/reading, absent from `shipped`) would linger on
// document.body after the flag flips OFF and leave converted consumers skin-styled.
const ALL_THEME_KEYS = [
    ...Object.keys(shipped.light), ...Object.keys(shipped.dark),
    ...Object.keys(skin.light), ...Object.keys(skin.dark),
].filter((k, i, a) => a.indexOf(k) === i);

// Single apply path for both theme setters (menu.jsx / exam-header.jsx). Applies the
// flag-selected value-set's custom properties on document.body, UNSETS any key absent
// from that set (B5 reversibility), and keeps the `data-skin` attribute in lockstep
// with the flag so the [data-skin]-scoped reading register (clinical-slate-type.css)
// activates on BOTH the env-flag and probe-attribute paths, in step with the inline
// color skin. SSR/jsdom-safe.
export function applyThemeVars(newTheme) {
    if (typeof document === 'undefined' || !document.body) return;
    const theme = themes[newTheme]; // flag-selected at access time (getter)
    const applied = Object.create(null);
    for (const key of Object.keys(theme)) {
        document.body.style.setProperty(`--${key}`, theme[key]);
        applied[key] = true;
    }
    for (const key of ALL_THEME_KEYS) {
        if (!applied[key]) document.body.style.removeProperty(`--${key}`);
    }
    if (document.documentElement) {
        if (isClinicalSlateSkin()) document.documentElement.setAttribute(SKIN_ATTR, SKIN_CLINICAL_SLATE);
        else document.documentElement.removeAttribute(SKIN_ATTR);
    }
}

// Named exports for the harness (CHECK1/CHECK2/parity) — scope-explicit, never
// flag-dependent, so tests can compare both value-sets deterministically.
export const shippedThemes = shipped;
export const skinThemes = skin;

export default themes;
